import { useLocalStorage } from '@uidotdev/usehooks';

import React, { useCallback, useMemo } from 'react';

import type { LoginResponse } from '@/__generated__/openapi-types';
import { Logger } from '@/lib/logger';

export type User = LoginResponse['user'];

export type AuthContext = {
  user: User | null;
  persistLogin: (user: User, accessToken: string, refreshToken: string) => void;
  isAuthenticated: boolean;
  setUserVerified: () => void;
};

const AuthContextElement = React.createContext<AuthContext | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useLocalStorage<User | null>('user', null);
  const [, setAccessToken] = useLocalStorage<string | null>(
    'accessToken',
    null
  );
  const [, setRefreshToken] = useLocalStorage<string | null>(
    'refreshToken',
    null
  );

  const persistLogin = useCallback(
    (user: User, accessToken: string, refreshToken: string) => {
      setUser(user);
      setAccessToken(accessToken);
      setRefreshToken(refreshToken);
      Logger.enrichUser(user);
    },
    [setUser, setAccessToken, setRefreshToken]
  );

  const setUserVerified = useCallback(() => {
    if (!user) {
      return;
    }
    setUser({ ...user, verified: true });
  }, [user, setUser]);

  const contextValue = useMemo<AuthContext>(
    () => ({
      user,
      persistLogin,
      isAuthenticated: !!user && user.verified,
      setUserVerified,
    }),
    [user, persistLogin, setUserVerified]
  );

  return (
    <AuthContextElement.Provider value={contextValue}>
      {children}
    </AuthContextElement.Provider>
  );
}

export function useAuth() {
  const context = React.useContext(AuthContextElement);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
